/**
 * Upload dialog for the Motorcycle Documents tab — supports Signed
 * Handover / Takeover forms today with the same form fields (Plate,
 * Date, Time, Lessee auto-filled, Rider dropdown filtered by lessee).
 *
 * The doc_type dropdown is deliberately shared for both today and
 * future docs so the same dialog can grow to accept Insurance / Passing
 * without a rewrite.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const DOC_TYPES = [
  { code: 'signed_handover', label: 'Signed Handover Form' },
  { code: 'signed_takeover', label: 'Signed Takeover Form' },
];

export default function UploadMotorcycleDocumentDialog({ open, onOpenChange, motorcycleId, onSaved }) {
  const [docType, setDocType] = useState('signed_handover');
  const [formDate, setFormDate] = useState(new Date().toISOString().slice(0, 10));
  const [formTime, setFormTime] = useState(new Date().toTimeString().slice(0, 5));
  const [riderId, setRiderId] = useState('');
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState({ motorcycle_plate: '', lessee_name: '', riders: [] });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setDocType('signed_handover');
      setFormDate(new Date().toISOString().slice(0, 10));
      setFormTime(new Date().toTimeString().slice(0, 5));
      setRiderId(''); setFile(null);
      return;
    }
    // Pull plate + lessee + rider dropdown from a single endpoint so
    // the dialog can autofill the Lessee field and only show riders
    // that actually belong to the same lessee as the motorcycle.
    (async () => {
      try {
        const r = await axios.get(`${API}/motorcycles/${motorcycleId}/riders-for-lessee`, getAuth());
        setMeta(r.data || { motorcycle_plate: '', lessee_name: '', riders: [] });
      } catch (e) {
        setMeta({ motorcycle_plate: '', lessee_name: '', riders: [] });
      }
    })();
  }, [open, motorcycleId]);

  const submit = async () => {
    if (!file) { toast.error('Please select the signed form file.'); return; }
    if (!riderId) { toast.error('Please select the rider.'); return; }
    setSubmitting(true);
    try {
      const rider = meta.riders.find(r => r.id === riderId);
      const fd = new FormData();
      fd.append('doc_type', docType);
      fd.append('file', file);
      if (formDate) fd.append('form_date', formDate);
      if (formTime) fd.append('form_time', formTime);
      if (rider) {
        fd.append('rider_id', rider.id);
        fd.append('rider_name', rider.rider_name || '');
      }
      await axios.post(`${API}/motorcycles/${motorcycleId}/documents/upload`, fd, {
        headers: { ...getAuth().headers, 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      toast.success('Document uploaded');
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="upload-motorcycle-doc-dialog">
        <DialogHeader>
          <DialogTitle>Upload Motorcycle Document</DialogTitle>
          <DialogDescription>Attach a signed handover / takeover form or other motorcycle document.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Document Type</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map(d => <SelectItem key={d.code} value={d.code}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Plate No.</Label>
              <Input value={meta.motorcycle_plate || ''} disabled data-testid="plate-number-field" />
            </div>
            <div>
              <Label className="text-xs">Lessee</Label>
              <Input value={meta.lessee_name || 'Not assigned'} disabled data-testid="lessee-name-field" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={formDate} onChange={(e) => setFormDate(e.target.value)} data-testid="form-date-input" />
            </div>
            <div>
              <Label className="text-xs">Time</Label>
              <Input type="time" value={formTime} onChange={(e) => setFormTime(e.target.value)} data-testid="form-time-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Rider Name</Label>
            <Select value={riderId} onValueChange={setRiderId}>
              <SelectTrigger data-testid="rider-select">
                <SelectValue placeholder={meta.riders.length ? 'Select rider…' : 'No riders under this lessee'} />
              </SelectTrigger>
              <SelectContent>
                {meta.riders.map(r => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.rider_name || r.rider_unique_id}{r.status && r.status !== 'active' ? ` · ${r.status}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {meta.lessee_name && !meta.riders.length && (
              <p className="text-xs text-amber-700 mt-1">No riders found under lessee &quot;{meta.lessee_name}&quot;.</p>
            )}
          </div>
          <div>
            <Label className="text-xs">Signed Form File (PDF / image)</Label>
            <Input type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="file-input" />
            {file && <p className="text-[10px] text-green-700 mt-1">{file.name} ({Math.round(file.size / 1024)} KB)</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !file || !riderId} data-testid="submit-upload-btn">
            {submitting ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
